/**
 * Booking-flow hero (promoted to shared in CWR 2.6; introduced in 2.5 §3-A/D1).
 * The BOOKING sits here (offering image + name + provider + when). Used by the
 * /details form page (2.5) AND the /payment page (2.6) — one shape regardless of
 * flow type: for event flows the `name` is already the event's own
 * CalendarEvent.title (resolved server-side, 2.5 §3-A). Faithful to the
 * booking-form / payment mockups' .hero / .hero-cap / .bk-line, on injected
 * client tokens.
 */
import { FocalImage } from '@/components/FocalImage';
import styles from './BookingHero.module.css';

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export interface BookingHeroProps {
  imageUrl: string | null;
  focalPoint: { x: number | null; y: number | null; scale?: number | null };
  name: string;
  providerName: string;
  providerPhoto: string | null;
  /** "Tue, Jun 17 · 6:30 PM" — the selected slot / event start; null when not yet known. */
  whenLabel: string | null;
  /** Caption kicker; approval-flow views read "Your request" (review/confirmation mockups). */
  eyebrow?: string;
  /** Reduce saturation for terminal states whose booking is no longer active. */
  muted?: boolean;
  /** Booking-detail caption: larger text, explicit spacing, and an emphasized relative date. */
  emphasizeWhenLabel?: boolean;
}

export function BookingHero({ imageUrl, focalPoint, name, providerName, providerPhoto, whenLabel, eyebrow = 'Your booking', muted = false, emphasizeWhenLabel = false }: BookingHeroProps) {
  const content = (
    <>
      <div className={styles.cap}>
        <div className={styles.eyebrow}>{eyebrow}</div>
        <div className={styles.name}>{name}</div>
        <div className={`${styles.when} ${emphasizeWhenLabel ? styles.whenEmphasized : ''}`}>
          <span
            className={styles.av}
            style={providerPhoto ? { backgroundImage: `url("${providerPhoto}")` } : undefined}
            aria-hidden="true"
          >
            {providerPhoto ? '' : initials(providerName)}
          </span>
          {emphasizeWhenLabel ? (
            <span className={styles.whenParts}>
              <span>with</span>
              <b>{providerName}</b>
              {whenLabel && <><span aria-hidden="true">·</span><b>{whenLabel}</b></>}
            </span>
          ) : (
            <span>
              with <b>{providerName}</b>
              {whenLabel ? ` · ${whenLabel}` : ''}
            </span>
          )}
        </div>
      </div>
    </>
  );

  return imageUrl ? (
    <FocalImage
      src={imageUrl}
      alt=""
      focalPointX={focalPoint.x}
      focalPointY={focalPoint.y}
      focalPointScale={focalPoint.scale}
      className={`${styles.hero} ${muted ? styles.heroMuted : ''}`}
      data-testid="booking-form-hero"
      data-muted={muted || undefined}
    >
      {content}
    </FocalImage>
  ) : (
    <div
      className={`${styles.hero} ${styles.heroFallback} ${muted ? styles.heroMuted : ''}`}
      data-testid="booking-form-hero"
      data-muted={muted || undefined}
    >
      {content}
    </div>
  );
}
